/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import {
	Directive,
	HostListener,
	type TemplateRef,
	effect,
	input,
	output,
} from "@angular/core";
import { takeUntil, tap } from "rxjs";
import { FloatingElement } from "../floating-element.directive";
import {
	type GroupedPopoverOption,
	PopoverComponent,
	type PopoverOption,
} from "./popover/popover.component";

@Directive({
	selector: "[cbPopover]",
	standalone: false,
})
export class PopoverDirective extends FloatingElement<PopoverComponent> {
	/**
	 * The trigger of the popover.
	 */
	public override readonly trigger = input<
		"click" | "focus" | "hover" | "contextmenu" | "none"
	>("click", { alias: "popoverTrigger" });

	/**
	 * The content of the popover.
	 */
	public readonly content = input<TemplateRef<unknown>>(undefined, {
		alias: "cbPopover",
	});

	/**
	 * If the popover is disabled
	 */
	public override readonly disabled = input(false, {
		alias: "popoverDisabled",
	});

	/**
	 * The popover distance from the target.
	 */
	public override readonly distance = input(2, { alias: "popoverDistance" });

	/**
	 * The options of the popover.
	 */
	public readonly options = input<PopoverOption[]>(undefined, {
		alias: "popoverOptions",
	});

	/**
	 * If the arrow should be shown.
	 */
	public readonly showArrow = input(false);

	/**
	 * If the popover should match the parent width.
	 */
	public override readonly matchParentWidth = input(false);

	/**
	 * The grouped options of the popover.
	 */
	public readonly groupedOptions = input<GroupedPopoverOption[]>(undefined, {
		alias: "popoverGroupedOptions",
	});

	/**
	 * The position of the floating element.
	 */
	public override readonly position = input<
		"top" | "bottom" | "left" | "right"
	>("bottom", { alias: "popoverPosition" });

	/**
	 * The context to pass to the template.
	 */
	public readonly context = input<Record<string, unknown>>(undefined, {
		alias: "popoverContext",
	});

	/**
	 * The template to use for the options.
	 */
	public readonly optionsTemplate = input<TemplateRef<unknown>>(undefined, {
		alias: "popoverOptionsTemplate",
	});

	/**
	 * Triggered when the popover has an option that is being selected.
	 */
	public readonly optionSelected = output<PopoverOption>();

	constructor() {
		super();

		this.component.set(PopoverComponent);

		effect(() => {
			this.setComponentProperties();
		});
	}

	/**
	 * Host listener for click events inside the componentRef.
	 */
	@HostListener("document:click", ["$event"])
	public docClick(event: MouseEvent): void {
		if (this.componentRef?.location.nativeElement.contains(event.target)) {
			if (this.closeOnInsideClick()) {
				this.close();
			}
		}
	}

	/**
	 * Checks if the popover has content to display.
	 * @returns boolean True if the popover has content to display.
	 */
	private hasContent(): boolean {
		const contentTemplate = this.content();
		const options = this.options();
		const groupedOptions = this.groupedOptions();

		return !!(
			contentTemplate ||
			(options && options.length > 0) ||
			(groupedOptions && groupedOptions.length > 0)
		);
	}

	/**
	 * Overrides the buildFloating method to check if there's content before building.
	 */
	public override buildFloating(): void {
		if (!this.hasContent()) {
			return;
		}

		super.buildFloating();
	}

	/**
	 * Sets the properties of the popover component.
	 */
	public override setComponentProperties(): void {
		if (!this.componentRef) return;

		this.componentRef.setInput("template", this.content() || null);

		this.componentRef.setInput("context", {
			...this.context(),
			close: () => this.close(),
		});

		const options = this.options();
		const groupedOptions = this.groupedOptions();

		if (options || groupedOptions) {
			this.componentRef.setInput("options", options ?? []);
			this.componentRef.setInput("groupedOptions", groupedOptions ?? []);

			this.componentRef.instance.optionClickedEvent
				.pipe(
					takeUntil(this.destroy$),
					tap((option: PopoverOption) => {
						this.optionSelected.emit(option);
						this.close();
					}),
				)
				.subscribe();
		}

		this.componentRef.setInput("showArrow", this.showArrow());
		this.componentRef.setInput("optionsTemplate", this.optionsTemplate());
	}

	override ngOnDestroy(): void {
		super.ngOnDestroy();
	}
}
